import React from 'react';
import { BOOKING_URL } from '../../../constants/config';
import {
  Container,
  Typography,
  Grid,
  Box,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  useMediaQuery,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Alert,
  AlertTitle,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import StarIcon from '@mui/icons-material/Star'; // For beautiful results/benefits
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'; // For nearly invisible
import EventSeatIcon from '@mui/icons-material/EventSeat'; // For comfort
import SpaIcon from '@mui/icons-material/Spa'; // For comfort / why choose us
import ComputerIcon from '@mui/icons-material/Computer'; // For digital planning/technology
import AllInclusiveIcon from '@mui/icons-material/AllInclusive'; // For series of aligners (continuous loop)
import RuleIcon from '@mui/icons-material/Rule'; // For straightening/alignment issues
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'; // For "Is it right for you?"
import WatchLaterIcon from '@mui/icons-material/WatchLater'; // For wear time
import CleaningServicesIcon from '@mui/icons-material/CleaningServices'; // For cleaning aligners
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser'; // For experienced providers
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';

const InvisalignPacificaDental = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const benefits = [
    'Virtually invisible aligners for discreet treatment.',
    'Removable for eating, drinking, brushing, and flossing with ease.',
    'Comfortable, smooth plastic aligners without sharp metal wires or brackets.',
    'Customized digital treatment planning for predictable and precise results.',
    'Often requires fewer office visits for adjustments compared to traditional braces.',
    'Boosts confidence as your smile gradually straightens.'
  ];

  const invisalignJourney = [
    {
      title: '1. Your Initial Invisalign® Consultation',
      description: 'At Pacifica Dental, your journey starts with a comprehensive consultation. We\'ll discuss your smile goals, assess your orthodontic needs, and determine if Invisalign® is the ideal solution for you. We\'ll answer all your questions and outline the process.',
      icon: <SpaIcon color="primary" sx={{ mr: 1, fontSize: '1.3rem' }} />
    },
    {
      title: '2. Digital Impressions & Custom Treatment Plan (ClinCheck®)',
      description: 'We use advanced digital scanning technology (like the iTero® scanner) to create precise 3D images of your teeth. With ClinCheck® software, we map out your entire treatment plan, allowing you to see a virtual representation of your tooth movements and final smile before you even begin.',
      icon: <ComputerIcon color="primary" sx={{ mr: 1, fontSize: '1.3rem' }} />
    },
    {
      title: '3. Receiving Your Custom Aligners',
      description: 'Based on your unique treatment plan, a series of custom-made, clear aligners are fabricated. You\'ll receive your initial sets from Pacifica Dental with detailed instructions on wearing them (typically 20-22 hours a day) and changing to a new set usually every 1-2 weeks.',
      icon: <AllInclusiveIcon color="primary" sx={{ mr: 1, fontSize: '1.3rem' }} />
    },
    {
      title: '4. Monitoring Your Progress',
      description: 'You\'ll have periodic check-up appointments at Pacifica Dental (usually every 6-8 weeks) so we can monitor your progress, ensure treatment is on track, and provide you with your next sets of aligners.',
      icon: <CheckCircleOutlineIcon color="primary" sx={{ mr: 1, fontSize: '1.3rem' }} />
    },
    {
      title: '5. Revealing Your New Smile & Retention',
      description: 'Once you\'ve completed your series of aligners, it\'s time to celebrate your beautiful, straight smile! To maintain your results, we will provide custom retainers (like Vivera® retainers) and instructions on their use.',
      icon: <StarIcon color="primary" sx={{ mr: 1, fontSize: '1.3rem' }} />
    }
  ];

  const issuesAddressed = [
    'Crowded or overly spaced teeth.',
    'Gaps between teeth.',
    'Overbite (upper teeth overlap lower teeth too much).',
    'Underbite (lower teeth protrude past upper teeth).',
    'Crossbite (some upper teeth sit inside lower teeth).',
    'Open bite (front upper and lower teeth don\'t meet).'
  ];

  const isRightForYou = [
    'You are committed to wearing your aligners for the recommended 20-22 hours per day.',
    'You desire a more discreet orthodontic solution compared to traditional braces.',
    'You have mild to moderate orthodontic concerns (our dentists at Pacifica Dental will determine suitability).',
    'You maintain good oral hygiene practices.',
    'You are a teen or adult looking to improve your smile.',
    'You understand the importance of wearing retainers after treatment to maintain results.'
  ];

  const livingWithInvisalign = [
    'Wear your aligners for 20-22 hours daily, only removing them to eat, drink (anything other than plain water), brush, and floss.',
    'Clean your aligners regularly as instructed by our team at Pacifica Dental (e.g., brushing gently with a soft toothbrush and rinsing).',
    'Brush and floss your teeth after every meal before reinserting your aligners to maintain oral health.',
    'Store your aligners in their protective case when not in use to prevent loss or damage.',
    'Change to your new set of aligners as directed by your dentist.',
    'Attend all scheduled check-up appointments at Pacifica Dental.'
  ];
  
  const faqs = [
    {
      question: 'How long does Invisalign® treatment typically take?',
      answer: 'Treatment time varies depending on the complexity of your case, but typically ranges from 6 to 18 months. Some simpler cases may take less time. Our dentists at Pacifica Dental will provide an estimated timeframe during your consultation.'
    },
    {
      question: 'Is Invisalign® treatment painful?',
      answer: 'You may experience some mild pressure or discomfort for a few days each time you switch to a new set of aligners, which is normal and indicates your teeth are moving. This discomfort is usually less than with traditional braces.'
    },
    {
      question: 'How often do I need to change my Invisalign® aligners?',
      answer: 'Typically, you will switch to a new set of aligners every 1 to 2 weeks, as prescribed by your dentist at Pacifica Dental.'
    },
    {
        question: 'Can I eat and drink normally with Invisalign®?',
        answer: 'Yes, one of the major advantages is that you remove your aligners for all meals and snacks. This means no food restrictions. Just remember to brush your teeth before putting them back in. You should only drink plain water while wearing your aligners.'
    },
    {
        question: 'What are Invisalign® attachments or "buttons"?',
        answer: 'Attachments are small, tooth-colored composite "buttons" that may be temporarily bonded to certain teeth. They act as handles to help the aligners grip the teeth more effectively and guide specific movements. They are discreet and removed after treatment.'
    }
  ];

  return (
    <>
      <Helmet>
        <title>Invisalign® Clear Aligners | Pacifica Dental | Straighten Your Smile</title>
        <meta name="description" content="Achieve a straighter, more confident smile discreetly with Invisalign® clear aligners at Pacifica Dental. Call (408) 869-0998 for a consultation." />
        <meta name="keywords" content="Invisalign Pacifica Dental, clear aligners, invisible braces, orthodontic treatment Pacifica Dental, teeth straightening, (408) 869-0998, San Jose CA Invisalign, Bay Area orthodontics" />
      </Helmet>
      
      <Box 
        sx={{ 
          py: { xs: 6, md: 8 },
          background: 'linear-gradient(135deg, #f5f7fa 0%, #e9eef3 100%)',
        }}
      >
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Typography
              variant={isMobile ? "h3" : "h2"}
              component="h1"
              textAlign="center"
              gutterBottom
              sx={{ 
                mb: 2,
                fontWeight: 700,
                color: 'primary.main'
              }}
            >
              Transform Your Smile Discreetly with Invisalign® at Pacifica Dental
            </Typography>

            <Typography
              variant={isMobile ? "h6" : "h5"}
              component="h2"
              textAlign="center"
              gutterBottom
              sx={{ mb: 4, color: 'text.secondary', fontWeight: 500 }}
            >
              The Clear Alternative to Traditional Braces for a Confident You
            </Typography>

            <Typography
              variant="body1"
              paragraph
              sx={{ mb: 4, fontSize: '1.15rem', lineHeight: 1.8, color: 'text.primary' }}
            >
              At Pacifica Dental, we are proud to offer Invisalign® clear aligners – a revolutionary way to straighten your teeth without the noticeable wires and brackets of traditional braces. Invisalign® uses a series of custom-made, virtually invisible plastic aligners to gently and gradually shift your teeth into their ideal positions. It's a comfortable, convenient, and discreet solution for adults and teens seeking a more confident smile.
            </Typography>
            <Divider sx={{ my: 4 }} />

            {/* Benefits Section */}
            <Grid container spacing={isMobile ? 3 : 5} alignItems="stretch">
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={3}
                  sx={{
                    p: isMobile ? 2 : 3,
                    height: '100%',
                    backgroundColor: theme.palette.mode === 'light' ? 'primary.light' : 'primary.dark',
                    color: 'white',
                    borderRadius: 2
                  }}
                >
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 2}}>
                    Advantages of Choosing Invisalign®
                  </Typography>
                  <List dense>
                    {benefits.map((benefit, index) => (
                      <ListItem key={index} sx={{ py: 0.5}}>
                        <ListItemIcon sx={{minWidth: 'auto', marginRight: 1.5}}>
                           {index === 0 ? <VisibilityOffIcon sx={{ color: 'white', fontSize: '1.4rem' }} /> : 
                            index === 1 ? <EventSeatIcon sx={{ color: 'white', fontSize: '1.4rem' }} /> : // Represents comfort/removability
                            <CheckCircleOutlineIcon sx={{ color: 'white', fontSize: '1.4rem' }} />
                           }
                        </ListItemIcon>
                        <ListItemText primary={benefit} primaryTypographyProps={{fontSize: '1.05rem'}} />
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </Grid>

              {/* Invisalign Journey Section */}
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={3}
                  sx={{
                    p: isMobile ? 2 : 3,
                    height: '100%',
                    borderRadius: 2
                  }}
                >
                  <Typography variant="h5" gutterBottom color="primary.main" sx={{ fontWeight: 600, mb: 2}}>
                    Your Invisalign® Journey at Pacifica Dental
                  </Typography>
                  {invisalignJourney.map((step, index) => (
                    <Box key={index} sx={{ mb: index === invisalignJourney.length - 1 ? 0 : 2.5 }}>
                       <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 0.5 }}>
                         {step.icon}
                        <Typography variant="h6" color="primary.dark" fontWeight="bold" sx={{fontSize: '1.1rem'}}>
                          {step.title}
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{fontSize: '0.95rem', lineHeight: 1.6, pl: '2.1rem'}}>
                        {step.description}
                      </Typography>
                    </Box>
                  ))}
                </Paper>
              </Grid>
            </Grid>

            <Divider sx={{ my: 5 }} /> 

            {/* What Issues Can Invisalign Address? Section */}
            <Box sx={{ my: 5 }}>
              <Typography variant={isMobile ? "h5" : "h4"} component="h3" gutterBottom textAlign="center" sx={{ fontWeight: 600, color: 'primary.main', mb: 3 }}>
                What Orthodontic Issues Can Invisalign® Correct?
              </Typography>
              <Paper elevation={2} sx={{p:3, borderRadius: 2, background: theme.palette.mode === 'light' ? '#e3f2fd' : '#2c3e50' }}>
                <Typography variant="body1" paragraph sx={{ mb: 2, fontSize: '1.1rem', color: 'text.secondary' }}>
                  Invisalign® is effective for treating a wide range of common orthodontic concerns, including:
                </Typography>
                <Grid container spacing={1}>
                    {issuesAddressed.map((item, index) => (
                        <Grid item xs={12} sm={6} md={4} key={index}>
                            <List dense sx={{py:0}}>
                                <ListItem sx={{py:0.2}}>
                                    <ListItemIcon sx={{minWidth: 'auto', marginRight: 1, color: 'primary.main'}}>
                                        <RuleIcon />
                                    </ListItemIcon>
                                    <ListItemText primary={item} primaryTypographyProps={{fontSize: '1rem'}}/>
                                </ListItem>
                            </List>
                        </Grid>
                    ))}
                </Grid>
              </Paper>
            </Box>

            <Divider sx={{ my: 5 }} />
            
            {/* Is Invisalign Right For You? Section */}
            <Box sx={{ my: 5 }}>
              <Typography variant={isMobile ? "h5" : "h4"} component="h3" gutterBottom textAlign="center" sx={{ fontWeight: 600, color: 'primary.main', mb: 3 }}>
                Is Invisalign® the Ideal Choice for Your Smile?
              </Typography>
              <Alert severity="info" icon={<HelpOutlineIcon />} sx={{borderRadius: 1.5, p:2, mb:3 }} elevation={2}>
                <AlertTitle sx={{fontWeight: 'bold'}}>Consult with Our Invisalign® Experts</AlertTitle>
                The best way to determine if Invisalign® is right for you is through a personalized consultation at Pacifica Dental. We'll assess your specific needs and discuss if this innovative treatment aligns with your smile goals.
              </Alert>
              <Grid container spacing={2}>
                {isRightForYou.map((item, index) => (
                  <Grid item xs={12} sm={6} key={index}>
                    <Paper elevation={1} sx={{p:1.5, display: 'flex', alignItems: 'center', height: '100%', borderRadius: 1.5}}>
                      <CheckCircleOutlineIcon color="success" sx={{mr: 1.5, fontSize: '1.8rem'}} />
                      <Typography variant="body1" sx={{fontSize: '1rem'}}>{item}</Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>

            <Divider sx={{ my: 5 }} />

            {/* Living with Invisalign Section */}
            <Box sx={{ my: 5 }}>
                <Typography variant={isMobile ? "h5" : "h4"} component="h3" gutterBottom textAlign="center" sx={{ fontWeight: 600, color: 'primary.main', mb: 3 }}>
                    Living with Invisalign®: Tips for Success
                </Typography>
                 <Paper elevation={2} sx={{p:3, borderRadius: 2, background: theme.palette.mode === 'light' ? '#e8f5e9' : '#1b5e20' }}>
                    <List>
                        {livingWithInvisalign.map((tip, index) => (
                            <ListItem key={index}>
                                <ListItemIcon sx={{minWidth: 'auto', marginRight: 1.5, color: 'primary.dark'}}>
                                    {index % 2 === 0 ? <WatchLaterIcon /> : <CleaningServicesIcon />}
                                </ListItemIcon>
                                <ListItemText primary={tip} primaryTypographyProps={{fontSize: '1rem'}}/>
                            </ListItem>
                        ))}
                    </List>
                </Paper>
            </Box>
            
            <Divider sx={{ my: 5 }} />

            {/* FAQ Section */}
            <Box sx={{ my: 5 }}>
              <Typography variant={isMobile ? "h5" : "h4"} component="h3" gutterBottom textAlign="center" sx={{ fontWeight: 600, color: 'primary.main', mb: 3 }}>
                Invisalign® FAQs at Pacifica Dental
              </Typography>
              {faqs.map((faq, index) => (
                <Accordion key={index} elevation={2} sx={{ mb: 1.5, borderRadius: 1, '&:before': { display: 'none' } }}>
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon color="primary"/>}
                    aria-controls={`faq-panel${index}-content`}
                    id={`faq-panel${index}-header`}
                    sx={{ '& .MuiAccordionSummary-content': { fontWeight: 'medium', fontSize: '1.1rem' } }}
                  >
                    {faq.question}
                  </AccordionSummary>
                  <AccordionDetails sx={{backgroundColor: theme.palette.mode === 'light' ? '#fafafa' : '#333' }}>
                    <Typography color="text.secondary" sx={{lineHeight: 1.7, fontSize: '1rem'}}>
                      {faq.answer}
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>

            <Divider sx={{ my: 5 }} />

            {/* Why Choose Pacifica Dental Section */}
            <Box sx={{ mt: 6, p:3, background: theme.palette.mode === 'light' ? 'rgba(0, 123, 255, 0.05)' : 'rgba(100,100,100,0.1)', borderRadius:2 }}>
              <Typography variant={isMobile ? "h5" : "h4"} component="h3" gutterBottom textAlign="center" sx={{ fontWeight: 600, color: 'primary.main', mb:3 }}>
                Why Choose Pacifica Dental for Your Invisalign® Treatment?
              </Typography>
              <Grid container spacing={3}>
                  {[
                      { title: "Experienced Invisalign® Providers", text: "Our dentists at Pacifica Dental are certified and experienced in providing Invisalign® treatment, ensuring you receive expert care.", icon: <VerifiedUserIcon color="primary" /> },
                      { title: "Advanced Digital Technology", text: "We utilize state-of-the-art digital scanning (like iTero®) and ClinCheck® software for precise planning and predictable outcomes.", icon: <ComputerIcon color="primary" /> },
                      { title: "Personalized Treatment Approach", text: "Your Invisalign® journey is tailored to your unique dental structure and aesthetic goals for the best possible results.", icon: <StarIcon color="primary" /> },
                      { title: "Commitment to Your Comfort & Satisfaction", text: "We prioritize your comfort throughout the treatment process and are dedicated to helping you achieve a smile you'll love.", icon: <SpaIcon color="primary" /> }
                  ].map(item => (
                      <Grid item xs={12} sm={6} key={item.title}>
                          <Box sx={{display: 'flex', alignItems: 'flex-start'}}>
                              <ListItemIcon sx={{minWidth: 'auto', marginRight: 1.5, marginTop: 0.5}}>
                                  {item.icon}
                              </ListItemIcon>
                              <Box>
                                  <Typography variant="h6" component="h4" sx={{fontWeight: 'bold', fontSize: '1.1rem'}}>{item.title}</Typography>
                                  <Typography variant="body2" color="text.secondary">{item.text}</Typography>
                              </Box>
                          </Box>
                      </Grid>
                  ))}
              </Grid>
            </Box>


            {/* Call to Action */}
            <Box sx={{ mt: 6, py: 4, textAlign: 'center', background: theme.palette.primary.main, color: 'white', borderRadius: 2 }}>
                <Typography variant={isMobile ? "h5" : "h4"} gutterBottom sx={{fontWeight: 600}}>
                    Ready for a Straighter Smile, the Clear Way?
                </Typography>
                <Typography variant="body1" sx={{mb:3, opacity: 0.9}}>
                    Discover the transformative power of Invisalign®. Contact Pacifica Dental today to schedule your personalized consultation!
                </Typography>
              <Button
                onClick={() => window.location.href = BOOKING_URL}
                variant="contained"
                color="secondary"
                size="large"
                sx={{
                  px: 5, 
                  py: 1.5,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '1.1rem',
                  '&:hover': {
                    backgroundColor: 'secondary.dark',
                  }
                }}
              >
                Schedule Your Invisalign Consultation
              </Button>
              <Typography variant="body2" sx={{mt: 2, opacity: 0.8}}>
                Or call Pacifica Dental at <Link to="tel:4088690998" style={{color: 'white', fontWeight: 'bold'}}>(408) 869-0998</Link> to learn more.
              </Typography>
            </Box>

          </motion.div>
        </Container>
      </Box>
    </>
  );
};

export default InvisalignPacificaDental;